/** @typedef {{ module: string, stylesheet?: string }} ComponentResources */
/** @type {Map<string, ComponentResources>} */
const optionalModules = new Map();
/** @type {Map<string, Promise<void>>} */
const pendingComponents = new Map();
/** @type {Map<string, Promise<unknown>>} */
const pendingModules = new Map();
/** @type {Map<string, Promise<void>>} */
const pendingStylesheets = new Map();
/** @type {Map<string, number>} */
const attempts = new Map();
/** @type {Set<string>} */
const failedComponents = new Set();
/** @type {Set<string>} */
const failedModules = new Set();
/** @type {Set<string>} */
const failedStylesheets = new Set();
/** @type {Error | undefined} */
let configurationError;
const configuration = document.getElementById('ThemeOptionalModules');

if (configuration) {
  try {
    const entries = JSON.parse(configuration.textContent || '{}');
    if (!entries || typeof entries !== 'object' || Array.isArray(entries)) throw new Error('Invalid component registry');
    for (const [tag, entry] of Object.entries(entries)) {
      const resources = typeof entry === 'string' ? { module: entry } : entry;
      if (
        !/^[a-z][a-z0-9]*-[a-z0-9-]+$/.test(tag) ||
        !resources || typeof resources.module !== 'string' || !resources.module ||
        (resources.stylesheet !== undefined && (typeof resources.stylesheet !== 'string' || !resources.stylesheet))
      ) throw new Error('Invalid component resources');
      optionalModules.set(tag, {
        module: new URL(resources.module, document.baseURI).href,
        ...(resources.stylesheet ? { stylesheet: new URL(resources.stylesheet, document.baseURI).href } : {}),
      });
    }
  } catch {
    configurationError = new Error('Invalid optional component configuration');
    document.dispatchEvent(new CustomEvent('theme:component-load-error', { detail: { code: 'INVALID_CONFIGURATION' } }));
  }
}

/** @param {string} source @returns {Promise<unknown>} */
function importModule(source) {
  const pending = pendingModules.get(source);
  if (pending) return pending;
  const attemptImport = () => {
    const attempt = attempts.get(source) || 0;
    attempts.set(source, attempt + 1);
    const url = new URL(source);
    if (attempt > 0) url.searchParams.set('theme_module_retry', String(attempt));
    return import(url.href);
  };
  const loading = attemptImport()
    .catch(async () => {
      await new Promise((resolve) => setTimeout(resolve, 750));
      return attemptImport();
    })
    .catch((error) => {
      failedModules.add(source);
      throw error;
    });
  pendingModules.set(source, loading);
  return loading;
}

/** @param {string} source @returns {Promise<void>} */
function loadStylesheet(source) {
  const pending = pendingStylesheets.get(source);
  if (pending) return pending;
  /** @returns {Promise<void>} */
  const attemptLoad = () => new Promise((resolve, reject) => {
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')]
      .find((link) => link instanceof HTMLLinkElement && link.href === source);
    const link = existing instanceof HTMLLinkElement ? existing : document.createElement('link');
    if (link.sheet) {
      resolve();
      return;
    }
    const cleanup = () => {
      clearTimeout(timeout);
      link.removeEventListener('load', onLoad);
      link.removeEventListener('error', onError);
    };
    const onLoad = () => {
      cleanup();
      delete link.dataset.styleFailed;
      resolve();
    };
    const onError = () => {
      cleanup();
      link.remove();
      reject(new Error('Component stylesheet failed to load'));
    };
    const timeout = setTimeout(onError, 15000);
    link.addEventListener('load', onLoad, { once: true });
    link.addEventListener('error', onError, { once: true });
    if (!existing) {
      link.rel = 'stylesheet';
      link.href = source;
      document.head.append(link);
    } else if (link.dataset.styleFailed === 'true') {
      onError();
    }
  });
  const loading = attemptLoad()
    .catch(async () => {
      await new Promise((resolve) => setTimeout(resolve, 750));
      await attemptLoad();
    })
    .catch((error) => {
      failedStylesheets.add(source);
      document.dispatchEvent(new CustomEvent('theme:component-load-error', {
        detail: { code: 'STYLESHEET_LOAD_FAILED' },
      }));
      throw error;
    });
  pendingStylesheets.set(source, loading);
  const clearPending = () => {
    if (pendingStylesheets.get(source) === loading) pendingStylesheets.delete(source);
  };
  void loading.then(clearPending, clearPending);
  return loading;
}

/** @param {string} tag @returns {Promise<void>} */
function waitForDefinition(tag) {
  if (customElements.get(tag)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error(`Component registration timed out: ${tag}`)), 15000);
    customElements.whenDefined(tag).then(() => {
      clearTimeout(timeout);
      resolve();
    }, (error) => {
      clearTimeout(timeout);
      reject(error);
    });
  });
}

/** @param {string} tag @param {ComponentResources} resources @returns {Promise<void>} */
function loadComponent(tag, resources) {
  // Registration can finish after a previous preparation timed out.
  if (failedComponents.has(tag) && customElements.get(tag)) {
    failedComponents.delete(tag);
    pendingComponents.delete(tag);
  }
  const pending = pendingComponents.get(tag);
  if (pending) return pending;
  const registration = (customElements.get(tag) ? Promise.resolve() : importModule(resources.module))
    .then(() => waitForDefinition(tag));
  const loading = Promise.all([
    registration,
    resources.stylesheet ? loadStylesheet(resources.stylesheet) : Promise.resolve(),
  ]).then(() => {
    if (!customElements.get(tag)) throw new Error(`Component not registered: ${tag}`);
    optionalModules.delete(tag);
  }).catch((error) => {
    failedComponents.add(tag);
    document.dispatchEvent(new CustomEvent('theme:component-load-error', {
      detail: { code: 'COMPONENT_LOAD_FAILED', component: tag },
    }));
    throw error;
  });
  pendingComponents.set(tag, loading);
  return loading;
}

/**
 * Waits for registered components and their styles, including disconnected fragments.
 * Rejects on failure so callers can preserve their current UI and retry safely.
 * @param {ParentNode} root
 * @returns {Promise<void>}
 */
export async function prepareComponents(root) {
  if (configurationError) throw configurationError;
  const styleSelector = 'link[data-theme-component-stylesheet], template[data-theme-component-stylesheet]';
  const links = [...root.querySelectorAll(styleSelector)];
  if (root instanceof Element && root.matches(styleSelector)) links.unshift(root);
  const sources = new Set(links
    .map((link) => link.getAttribute('href') || link.getAttribute('data-href'))
    .filter(Boolean)
    .map((href) => new URL(href, document.baseURI).href));
  await Promise.all([...sources].map(loadStylesheet));
  const loading = [];
  for (const [tag, resources] of optionalModules) {
    if ((root instanceof Element && root.matches(tag)) || root.querySelector(tag)) {
      loading.push(loadComponent(tag, resources));
    }
  }
  await Promise.all(loading);
}

/** @type {Set<Element>} */
const addedRoots = new Set();
let flushScheduled = false;

/** @param {ParentNode} root @returns {void} */
function prepareObserved(root) {
  // Failures are reported by loadComponent; observers have no caller to reject to.
  void prepareComponents(root).catch(() => {});
}

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element && node.isConnected) addedRoots.add(node);
    }
  }
  if (flushScheduled || addedRoots.size === 0) return;
  flushScheduled = true;
  queueMicrotask(() => {
    flushScheduled = false;
    const roots = new Set(addedRoots);
    addedRoots.clear();
    for (const root of roots) {
      if (!root.isConnected) continue;
      let ancestor = root.parentElement;
      while (ancestor && !roots.has(ancestor)) ancestor = ancestor.parentElement;
      if (ancestor) continue;
      prepareObserved(root);
    }
  });
});

observer.observe(document.documentElement, { childList: true, subtree: true });
prepareObserved(document);
window.addEventListener('online', () => {
  for (const source of failedModules) pendingModules.delete(source);
  for (const source of failedStylesheets) pendingStylesheets.delete(source);
  for (const tag of failedComponents) pendingComponents.delete(tag);
  failedModules.clear();
  failedStylesheets.clear();
  failedComponents.clear();
  prepareObserved(document);
});
